import * as AntdIcons from '@ant-design/icons';
import type { MenuDataItem } from '@ant-design/pro-components';
import type {
  AppRouteMap,
  AppRouteNode,
  BackendRouteItem,
  RoutePermissionContext,
} from '@/types/route';

const normalizeBoolean = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();

    if (!normalizedValue) {
      return false;
    }

    if (['1', 'true', 'yes', 'y'].includes(normalizedValue)) {
      return true;
    }

    if (['0', 'false', 'no', 'n'].includes(normalizedValue)) {
      return false;
    }

    return false;
  }

  return false;
};

// 统一修正后端 path，避免出现双斜杠、相对路径、尾部斜杠不一致等问题。
const normalizePath = (path: string) => {
  if (!path) {
    return '/';
  }

  const prefixedPath = path.startsWith('/') ? path : `/${path}`;
  const compactedPath = prefixedPath.replace(/\/{2,}/g, '/');

  if (compactedPath !== '/' && compactedPath.endsWith('/')) {
    return compactedPath.slice(0, -1);
  }

  return compactedPath;
};

const joinPaths = (parentPath: string, currentPath: string) => {
  if (!currentPath) {
    return normalizePath(parentPath);
  }

  if (currentPath.startsWith('/')) {
    return normalizePath(currentPath);
  }

  return normalizePath(`${parentPath}/${currentPath}`);
};

const hasAccess = (
  route: Pick<BackendRouteItem, 'roles' | 'permissions'>,
  permissionContext: RoutePermissionContext,
) => {
  const rolePassed =
    !route.roles?.length ||
    route.roles.some((role) => permissionContext.roles.includes(role));
  const permissionPassed =
    !route.permissions?.length ||
    route.permissions.every((permission) =>
      permissionContext.permissions.includes(permission),
    );

  return rolePassed && permissionPassed;
};

// 支持两种格式：
//   camelCase  → 自动补 Outlined 后缀，如 'team' → 'TeamOutlined'
//   PascalCase → 直接查找，如 'TeamOutlined' / 'TeamFilled'
// 其他格式（含空格、连字符等）视为无效，返回 undefined。
const resolveIcon = (icon?: string) => {
  if (!icon) return undefined;

  const isCamelCase = /^[a-z][a-zA-Z0-9]*$/.test(icon);
  const isPascalCase = /^[A-Z][a-zA-Z0-9]*$/.test(icon);

  if (!isCamelCase && !isPascalCase) return undefined;

  const componentName = isCamelCase
    ? icon.charAt(0).toUpperCase() + icon.slice(1) + 'Outlined'
    : icon;

  const IconComponent = (AntdIcons as Record<string, unknown>)[componentName] as
    | React.ComponentType
    | undefined;

  return IconComponent ? <IconComponent /> : undefined;
};

const findFirstLeafRoute = (
  routes: AppRouteNode[],
  includeHidden = false,
): AppRouteNode | undefined => {
  for (const route of routes) {
    if (
      !route.children.length &&
      route.componentKey &&
      (includeHidden || !route.hideInMenu)
    ) {
      return route;
    }

    const childLeaf = findFirstLeafRoute(route.children, includeHidden);
    if (childLeaf) {
      return childLeaf;
    }
  }

  return undefined;
};

const collectDescendantPaths = (routes: AppRouteNode[]) => {
  const pathSet = new Set<string>();

  const walk = (items: AppRouteNode[]) => {
    items.forEach((item) => {
      pathSet.add(item.path);
      walk(item.children);
    });
  };

  walk(routes);
  return pathSet;
};

const flattenRoutes = (routes: AppRouteNode[]): AppRouteNode[] => {
  return routes.flatMap((route) => [route, ...flattenRoutes(route.children)]);
};

const isContainerOnlyRoute = (route: Pick<AppRouteNode, 'componentKey'>) => {
  return !route.componentKey || route.componentKey === 'common/com';
};

const buildRedirect = (
  route: BackendRouteItem,
  currentPath: string,
  children: AppRouteNode[],
) => {
  if (!children.length) {
    return undefined;
  }

  const descendantPaths = collectDescendantPaths(children);
  const candidate = route.redirect
    ? joinPaths(currentPath, route.redirect)
    : undefined;

  if (
    candidate &&
    !candidate.includes('..') &&
    descendantPaths.has(candidate)
  ) {
    return candidate;
  }

  const firstVisibleChild = children.find((child) => !child.hideInMenu);
  if (!firstVisibleChild) {
    return undefined;
  }

  // 菜单点击默认只进入可见子菜单；如果可见子菜单本身就是页面，
  // 就停在它自己，不再因为隐藏详情页被自动重定向走。
  if (firstVisibleChild.redirect) {
    return firstVisibleChild.redirect;
  }

  if (!isContainerOnlyRoute(firstVisibleChild)) {
    return firstVisibleChild.path;
  }

  return findFirstLeafRoute(firstVisibleChild.children)?.path;
};

const toMenuData = (routes: AppRouteNode[]): MenuDataItem[] => {
  return routes
    .filter((route) => !route.hideInMenu)
    .map((route) => ({
      key: route.path,
      path: route.path,
      name: route.name,
      icon: resolveIcon(route.icon),
      level: route.level,
      children: toMenuData(route.children),
    }));
};

const mapRoutes = (
  routes: BackendRouteItem[],
  permissionContext: RoutePermissionContext,
  parentPath = '',
  level = 0,
): AppRouteNode[] => {
  return (
    routes
      // 路由树构建阶段就把没权限的节点裁掉，菜单和页面访问天然保持一致。
      .filter((route) => hasAccess(route, permissionContext))
      .map((route) => {
        const currentPath = joinPaths(parentPath || '/', route.path);
        const children = mapRoutes(
          Array.isArray(route.children) ? route.children : [],
          permissionContext,
          currentPath,
          level + 1,
        );
        const hideInMenu = normalizeBoolean(
          (route as { hideInMenu?: unknown }).hideInMenu,
        );

        return {
          name: route.name,
          path: currentPath,
          rawPath: route.path,
          icon: route.icon,
          componentKey: route.componentKey,
          hideInMenu,
          redirect: buildRedirect(route, currentPath, children),
          roles: route.roles,
          permissions: route.permissions,
          children,
          level,
        };
      })
  );
};

export const buildRouteManifest = (
  routes: BackendRouteItem[],
  permissionContext: RoutePermissionContext,
) => {
  // 一次构建出路由树、扁平映射、菜单数据，运行时直接消费，避免重复计算。
  const routeTree = mapRoutes(routes, permissionContext);
  const flatRoutes = flattenRoutes(routeTree);
  const routeMap = flatRoutes.reduce<AppRouteMap>((map, route) => {
    map[route.path] = route;
    return map;
  }, {});

  return {
    routeTree,
    routeMap,
    menuData: toMenuData(routeTree),
    firstLeafPath:
      findFirstLeafRoute(routeTree)?.path ||
      findFirstLeafRoute(routeTree, true)?.path,
  };
};

export const getMatchedRoute = (
  routeMap: AppRouteMap,
  pathname: string,
): AppRouteNode | undefined => {
  return routeMap[normalizePath(pathname)];
};

export { normalizePath };
